import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconX } from '@tabler/icons-react';
import type { NotificationFeedItem } from '../../notifications/types';
import { AppDialogFrame } from '../overlays/AppDialogFrame';
import { handleNotificationNavigationError } from './notificationNavigationSafety';

export type NotificationMenuTone = 'urgent' | 'status' | 'message' | 'system';

export type NotificationMenuItem = {
  id: string;
  text: string;
  timeLabel: string;
  tone: NotificationMenuTone;
  isUnread: boolean;
  applicationId: string | null;
  familyId: string | null;
};

export type CrossHeaderNotificationsMenuProps = {
  testIdPrefix: string;
  open: boolean;
  unreadCount: number;
  items: NotificationMenuItem[];
  isLoading?: boolean;
  error?: string | null;
  previewLimit?: number;
  emptyLabel?: string;
  onSelectItem?: (item: NotificationMenuItem) => void | Promise<void>;
  onClose?: () => void;
};

const toRelativeTimeLabel = (timestamp: number): string => {
  if (!Number.isFinite(timestamp) || timestamp <= 0) {
    return 'JUST NOW';
  }
  // Some feeds still return epoch seconds.
  const normalizedTimestamp = timestamp > 1_000_000_000_000 ? timestamp : timestamp * 1000;
  const deltaMs = Math.max(0, Date.now() - normalizedTimestamp);
  const deltaMinutes = Math.floor(deltaMs / 60000);
  if (deltaMinutes < 1) return 'JUST NOW';
  if (deltaMinutes < 60) return `${deltaMinutes}M AGO`;
  const deltaHours = Math.floor(deltaMinutes / 60);
  if (deltaHours < 24) return `${deltaHours}H AGO`;
  const deltaDays = Math.floor(deltaHours / 24);
  return `${deltaDays}D AGO`;
};

const withNotificationPrefix = (subject: string, status: string): string => {
  if (subject.includes(':')) {
    return subject;
  }
  const lowered = subject.toLowerCase();
  if (lowered.includes('document')) return `Documents: ${subject}`;
  if (lowered.includes('message')) return `Message: ${subject}`;
  if (lowered.includes('inquiry')) return `New Inquiry: ${subject}`;
  if (lowered.includes('status')) return `Status Update: ${subject}`;
  if (status.toLowerCase() === 'read') return `System: ${subject}`;
  return `Status Update: ${subject}`;
};

const resolveTone = (text: string, unreadCount: number): NotificationMenuTone => {
  const lowered = text.toLowerCase();
  if (lowered.includes('new inquiry') || lowered.includes('urgent') || lowered.includes('interview')) {
    return 'urgent';
  }
  if (
    lowered.includes('status')
    || lowered.includes('in-progress')
    || lowered.includes('decision')
  ) {
    return 'status';
  }
  if (
    lowered.includes('message')
    || lowered.includes('documents')
    || lowered.includes('comment')
    || lowered.includes('upload')
  ) {
    return 'message';
  }
  return unreadCount > 0 ? 'status' : 'system';
};

const splitText = (text: string): { prefix: string; content: string } => {
  const [prefix, ...rest] = text.split(':');
  if (!prefix || rest.length === 0) {
    return { prefix: '', content: text };
  }
  return {
    prefix: prefix.trim(),
    content: rest.join(':').trim(),
  };
};

const toneDotClassName = (tone: NotificationMenuTone): string => {
  if (tone === 'urgent') return 'bg-[#fe2c3b]';
  if (tone === 'status') return 'bg-[#28e2a7]';
  if (tone === 'message') return 'bg-[#92eeba]';
  return 'bg-[#7cd7b7]';
};

export const mapCrossHeaderNotificationsToMenuItems = (
  items: NotificationFeedItem[]
): NotificationMenuItem[] =>
  items.map((item) => {
    const text = withNotificationPrefix(item.subject, item.status);
    return {
      id: item.id,
      text,
      timeLabel: toRelativeTimeLabel(item.lastUpdatedAt),
      tone: resolveTone(text, item.unreadCount),
      isUnread: item.isUnread,
      applicationId: item.applicationId ?? null,
      familyId: item.familyId ?? null,
    };
  });

export const CrossHeaderNotificationsMenu: React.FC<CrossHeaderNotificationsMenuProps> = ({
  testIdPrefix,
  open,
  unreadCount,
  items,
  isLoading = false,
  error = null,
  previewLimit = 5,
  emptyLabel = 'No notifications.',
  onSelectItem,
  onClose,
}) => {
  const [isActivityOpen, setIsActivityOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const previewItems = useMemo(
    () => items.slice(0, Math.max(1, previewLimit)),
    [items, previewLimit]
  );
  const renderedPreviewItems = previewItems;
  const renderedTimelineItems = items;
  const effectiveUnreadCount = Math.max(0, unreadCount);

  useEffect(() => {
    if (!open || typeof window === 'undefined' || typeof document === 'undefined') {
      return () => {};
    }

    const updatePosition = () => {
      const toggle = document.querySelector(
        `[data-testid="${testIdPrefix}-notifications-toggle"]`
      ) as HTMLElement | null;
      if (!toggle) return;
      const rect = toggle.getBoundingClientRect();
      const isMobile = window.innerWidth < 768;
      setDropdownPos({
        top: rect.bottom + window.scrollY + 12,
        left: isMobile ? 27 : rect.right + window.scrollX - 320,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, { passive: true });
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [open, testIdPrefix]);

  if (!open && !isActivityOpen) {
    return null;
  }

  const handleSelectItem = (item: NotificationMenuItem) => {
    try {
      void Promise.resolve(onSelectItem?.(item)).catch((error) => {
        handleNotificationNavigationError(
          '[CrossHeaderNotificationsMenu] Notification item selection failed',
          error,
        );
      });
    } catch (error) {
      handleNotificationNavigationError(
        '[CrossHeaderNotificationsMenu] Notification item selection failed',
        error,
      );
    }
  };

  const renderItem = (item: NotificationMenuItem) => {
    const parts = splitText(item.text);
    return (
      <button
        key={item.id}
        type="button"
        className="flex w-full items-start gap-3 border-b border-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-50/50 last:border-0"
        data-testid={`${testIdPrefix}-notification-item`}
        data-notification-status={item.isUnread ? 'unread' : 'read'}
        data-notification-unread={item.isUnread ? 'true' : 'false'}
        onClick={() => handleSelectItem(item)}
      >
        <span className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${toneDotClassName(item.tone)}`} />
        <span className="min-w-0 flex-1">
          <span className="text-sm leading-relaxed text-irongrey">
            {parts.prefix ? <span className="font-bold text-irongrey">{parts.prefix}:</span> : null}
            {parts.content ? ` ${parts.content}` : item.text}
          </span>
          <span className="mt-1 block text-[10px] font-bold uppercase tracking-wide text-irongrey/40">
            {item.timeLabel}
          </span>
        </span>
      </button>
    );
  };

  const notificationsPanel = open ? (
    <div
      style={{
        top: `${dropdownPos.top}px`,
        left: `${dropdownPos.left}px`,
        width: '320px',
      }}
      className="fixed overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-xl z-[200]"
      data-testid={`${testIdPrefix}-notifications-panel`}
    >
      <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50/30 px-4 py-3">
        <span className="text-[10px] font-bold uppercase tracking-widest text-irongrey/40">Notifications</span>
        {effectiveUnreadCount > 0 ? (
          <span
            className="rounded-full bg-[#28e2a7]/10 px-2 py-0.5 text-[10px] font-bold text-[#28e2a7]"
            data-testid={`${testIdPrefix}-notifications-new-badge`}
          >
            {`${effectiveUnreadCount} New`}
          </span>
        ) : null}
      </div>

      <div
        className="max-h-96 overflow-y-auto"
        data-testid={`${testIdPrefix}-notifications-list`}
        data-loading={isLoading ? 'true' : 'false'}
        data-error={error ? 'true' : 'false'}
      >
        {renderedPreviewItems.length > 0
          ? renderedPreviewItems.map(renderItem)
          : <div className="px-4 py-4 text-sm text-irongrey/60">{emptyLabel}</div>}
      </div>

      <div className="border-t border-gray-100 bg-gray-50/30 p-2">
        <button
          type="button"
          className="w-full rounded-lg py-2 text-center text-[10px] font-bold uppercase tracking-widest text-[#28e2a7] transition-colors hover:bg-[#28e2a7]/5"
          data-testid={`${testIdPrefix}-notifications-view-all`}
          onClick={() => {
            setIsActivityOpen(true);
            onClose?.();
          }}
        >
          View All Activity
        </button>
      </div>
    </div>
  ) : null;

  return (
    <>
      {notificationsPanel && typeof document !== 'undefined'
        ? createPortal(notificationsPanel, document.body)
        : notificationsPanel}
      {isActivityOpen && typeof document !== 'undefined'
        ? createPortal(
            <AppDialogFrame
              open={isActivityOpen}
              onClose={() => setIsActivityOpen(false)}
              title="Activity Timeline"
              description="Tracking all updates and system events"
              closeIcon={<IconX size={18} />}
              testId={`${testIdPrefix}-activity-timeline-modal`}
              maxWidthClassName="max-w-2xl"
              bodyPaddingClassName="p-2"
              titleClassName="text-xl"
              descriptionClassName="text-xs text-irongrey/40"
              footerPaddingClassName="p-4"
              footerClassName="justify-center text-xs text-irongrey/40"
              footer={<span>End of history</span>}
            >
              {renderedTimelineItems.length
                ? renderedTimelineItems.map(renderItem)
                : <div className="px-4 py-4 text-sm text-irongrey/60">{emptyLabel}</div>}
            </AppDialogFrame>,
            document.body
          )
        : null}
    </>
  );
};
